/**
 * dom-i18n.js — automatic DOM text-node translation.
 *
 * Instead of wrapping every string in `<T>`, we walk the rendered DOM,
 * collect visible English text nodes and their user-facing attributes
 * (aria-label, placeholder, title), and replace them with translations
 * fetched from `/api/wallet/translate/batch` (cached in localStorage
 * per-language on the frontend, hashed to disk on the backend).
 *
 * How it stays correct across React re-renders
 *   • For every text node we translate, we keep the ORIGINAL English
 *     in a WeakMap keyed by the node itself. React re-renders create
 *     *new* text nodes → not in WeakMap → we treat them as fresh
 *     English input → translate again (usually instant, from cache).
 *   • A MutationObserver watches for new nodes / attribute changes,
 *     debounced via `requestAnimationFrame` so bursts of React
 *     updates coalesce into one sweep.
 *
 * What we skip
 *   • Structural / non-visible tags (`SCRIPT`, `STYLE`, `CODE`, `PRE`,
 *     `INPUT`, `TEXTAREA`, `SVG` and their children).
 *   • Anything inside `[data-no-translate]` — opt-out escape hatch.
 *   • Pure numbers, ISO dates, currency (`R15 000`), IDs
 *     (`AC-000000`, `dev-xxx`), URLs, emails.
 *   • Very short strings (`< 3` chars) which are usually icons or
 *     status glyphs.
 */

const ORIGINALS = new WeakMap();        // TextNode → original English
const ATTR_ORIGINALS = new WeakMap();   // Element  → { attr → original }

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "CODE", "PRE", "INPUT", "TEXTAREA",
  "NOSCRIPT", "SVG", "PATH", "OPTION",
]);

// Only these attributes are consumer-visible strings worth translating.
const TRANSLATABLE_ATTRS = ["placeholder", "aria-label", "title", "alt"];

const NUMERIC_ONLY   = /^[\s\d.,%:_/+\-–—()]+$/;
const CURRENCY_ZAR   = /^R\s?[\d\s.,]+$/;
const ID_LIKE        = /^([A-Z]{1,4}[-_]\d|dev[-_]|[a-f0-9]{16,})/i;
const URL_LIKE       = /https?:\/\//i;
const EMAIL_LIKE     = /\S+@\S+\.\S+/;

function shouldTranslateText(node) {
  if (!node || node.nodeType !== 3) return false;
  const parent = node.parentElement;
  if (!parent) return false;
  if (SKIP_TAGS.has(parent.tagName)) return false;
  // Walk up looking for opt-out
  let el = parent;
  while (el && el !== document.body) {
    if (el.hasAttribute && el.hasAttribute("data-no-translate")) return false;
    if (SKIP_TAGS.has(el.tagName)) return false;
    el = el.parentElement;
  }
  const raw = node.nodeValue;
  if (!raw) return false;
  const trimmed = raw.trim();
  if (trimmed.length < 3) return false;
  if (NUMERIC_ONLY.test(trimmed)) return false;
  if (CURRENCY_ZAR.test(trimmed)) return false;
  if (ID_LIKE.test(trimmed)) return false;
  if (URL_LIKE.test(trimmed)) return false;
  if (EMAIL_LIKE.test(trimmed)) return false;
  // Skip strings that look like they came from another translation
  // (all-caps single-word acronyms — regulator names, brand tokens).
  if (/^(NAVI|FSCA|POPIA|COFI|TCF|FAIS|FICA|SARB|CIPC|SARS)$/.test(trimmed)) return false;
  return true;
}

function isTranslatableAttr(el, attr) {
  if (!el || !attr) return false;
  const v = el.getAttribute(attr);
  if (!v) return false;
  const trimmed = v.trim();
  if (trimmed.length < 3) return false;
  if (NUMERIC_ONLY.test(trimmed)) return false;
  if (URL_LIKE.test(trimmed)) return false;
  return true;
}

function collectFromRoot(root) {
  const texts = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (shouldTranslateText(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  let n;
  while ((n = walker.nextNode())) texts.push(n);

  const attrs = [];
  for (const attr of TRANSLATABLE_ATTRS) {
    const elements = root.querySelectorAll ? root.querySelectorAll(`[${attr}]`) : [];
    for (const el of elements) {
      // Opt-out
      if (el.closest && el.closest("[data-no-translate]")) continue;
      if (SKIP_TAGS.has(el.tagName)) continue;
      if (isTranslatableAttr(el, attr)) attrs.push({ el, attr });
    }
  }
  return { texts, attrs };
}

/**
 * DomTranslator — attach to a root element; sweeps it whenever
 * `refresh()` is called (on language change or new translations
 * arriving). Uses a MutationObserver to catch React re-renders.
 */
export class DomTranslator {
  constructor(root, deps) {
    this.root = root;
    this.deps = deps;   // { getLanguage(), getTranslations(), enqueue(strings[]) }
    this.observer = null;
    this._raf = null;
  }

  start() {
    this._sweep();
    this.observer = new MutationObserver(() => this._scheduleSweep());
    this.observer.observe(this.root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRS,
    });
  }

  stop() {
    this.observer?.disconnect();
    this.observer = null;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
  }

  /**
   * Restore every previously-modified node/attribute to its English
   * original. Called when the user picks English again.
   */
  restoreEnglish() {
    // Text nodes — walk root, restore anything in WeakMap
    const walker = document.createTreeWalker(this.root, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
      const orig = ORIGINALS.get(n);
      if (orig != null && n.nodeValue !== orig) n.nodeValue = orig;
    }
    // Attributes — walk elements with tracked attribute originals
    for (const attr of TRANSLATABLE_ATTRS) {
      const els = this.root.querySelectorAll ? this.root.querySelectorAll(`[${attr}]`) : [];
      for (const el of els) {
        const bag = ATTR_ORIGINALS.get(el);
        if (bag && bag[attr] != null && el.getAttribute(attr) !== bag[attr]) {
          el.setAttribute(attr, bag[attr]);
        }
      }
    }
  }

  /** Public: request a sweep (called by provider when translations arrive). */
  refresh() { this._scheduleSweep(); }

  _scheduleSweep() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this._sweep();
    });
  }

  _sweep() {
    const lang = this.deps.getLanguage();
    if (lang === "en") return;
    const translations = this.deps.getTranslations();
    const { texts, attrs } = collectFromRoot(this.root);
    const pending = new Set();

    for (const n of texts) {
      // Determine the ORIGINAL English for this node
      let original = ORIGINALS.get(n);
      if (original == null) {
        original = n.nodeValue;
        ORIGINALS.set(n, original);
      }
      const key = original.trim();
      if (!key) continue;
      const hit = translations[key];
      if (hit != null) {
        const leading  = original.match(/^\s*/)[0];
        const trailing = original.match(/\s*$/)[0];
        const nextVal  = leading + hit + trailing;
        if (n.nodeValue !== nextVal) n.nodeValue = nextVal;
      } else {
        pending.add(key);
      }
    }

    for (const { el, attr } of attrs) {
      let bag = ATTR_ORIGINALS.get(el);
      if (!bag) { bag = {}; ATTR_ORIGINALS.set(el, bag); }
      if (bag[attr] == null) bag[attr] = el.getAttribute(attr);
      const original = bag[attr];
      const key = original.trim();
      if (!key) continue;
      const hit = translations[key];
      if (hit != null) {
        if (el.getAttribute(attr) !== hit) el.setAttribute(attr, hit);
      } else {
        pending.add(key);
      }
    }

    if (pending.size > 0) this.deps.enqueue([...pending]);
  }
}
